import EmptyState from "@/components/empty-state/EmptyState.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import {
  ConfirmationModal,
  Page,
  useModals,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Table,
  TableCell,
} from "@skailer-com/uikit";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { LegalEntityEditModal } from "@/pages/settings/legal-entities/widgets/LegalEntityEditModal.tsx";
import { useLegalEntities, useLegalEntityDelete } from "@/queries/use-legal-entities.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiLegalEntity } from "@/types/legal-entity.types.ts";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export default function LegalEntities() {
  const { t } = useTranslation();
  const { renderLocationAddress } = useFormatter();

  useTitle(t("core:legal_entities"), t("common:settings"));

  const { openModal } = useModals();

  const { data: legalEntities, isLoading, isFetching } = useLegalEntities();
  const legalEntityDelete = useLegalEntityDelete();

  const columns = useMemo<ColumnDef<ApiLegalEntity>[]>(
    () => [
      {
        header: t("common:title"),
        cell: (row) => <TableCell>{row.row.original.title}</TableCell>,
        accessorKey: "title",
        ...ColumnSizes.employee,
      },
      {
        header: t("core:address"),
        cell: (row) => (
          <TableCell variant={"secondary"} className={"whitespace-pre-wrap"}>
            {renderLocationAddress(row.row.original.location)}
          </TableCell>
        ),
        accessorKey: "address",
        ...ColumnSizes.employee,
      },
      {
        header: t("common:tax_id"),
        cell: (row) => <TableCell variant={"secondary"}>{row.row.original.tax_id}</TableCell>,
        accessorKey: "tax_id",
        ...ColumnSizes.status,
      },
      {
        header: t("common:currency"),
        cell: (row) => (
          <TableCell variant={"secondary"}>{row.row.original.currency?.code}</TableCell>
        ),
        accessorKey: "currency",
        ...ColumnSizes.status,
      },
      {
        header: t("core:employees"),
        cell: (row) => <div className={"text-right px-2"}>{row.row.original.employees_count}</div>,
        accessorKey: "employees_count",
        ...ColumnSizes.status,
      },
    ],
    [legalEntities]
  );

  const handleEditOrCreate = useCallback((legalEntity: ApiLegalEntity | undefined = undefined) => {
    openModal(LegalEntityEditModal, {
      legalEntityId: legalEntity?.id || 0,
      legalEntity: legalEntity,
    });
  }, []);

  const handleDeleteAction = useCallback((legalEntity: ApiLegalEntity) => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await legalEntityDelete.mutateAsync(legalEntity.id, {
            onSuccess: () => {
              analyticsService.trackEvent(analyticEvents.legalEntities.deleted, {
                [analyticProperties.id]: legalEntity.id,
              });
            },
          });
        } catch {}
      },
    });
  }, []);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.legalEntities.viewed);
  });

  const renderRowAction = useCallback(
    (row: Row<ApiLegalEntity>) => (
      <DropdownMenu>
        <DropdownMenuItem
          onClick={() => {
            handleEditOrCreate(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
          title={t("common:edit")}
        />
        <DropdownMenuItem
          onClick={() => {
            handleDeleteAction(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
          variant={"menuDanger"}
          title={t("common:delete") + "..."}
        />
      </DropdownMenu>
    ),
    [handleDeleteAction, handleEditOrCreate, t]
  );

  return (
    <>
      <Page>
        <Page.Header
          showBack={true}
          title={t("core:legal_entities")}
          navigateTo={"/settings/company-structure"}
        >
          <Stack>
            <Button
              onClick={() => {
                handleEditOrCreate();
              }}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
            >
              {t("common:create_new")}
            </Button>
          </Stack>
        </Page.Header>
        <Page.Content>
          <Table
            renderRowAction={renderRowAction}
            data={legalEntities || []}
            columns={columns}
            isFetching={isFetching || legalEntityDelete.isPending}
            showSkeleton={isLoading}
            notFoundComponent={<EmptyStateNothingFound />}
            emptyStateComponent={<EmptyState />}
          />
        </Page.Content>
      </Page>
    </>
  );
}
